import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { 
  Globe, 
  Mail, 
  Send, 
  TrendingUp, 
  ArrowRight,
  Zap,
  Target,
  BarChart3
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { getDashboardStats } from "../lib/api";
import { motion } from "framer-motion";

const MetricCard = ({ title, value, subtitle, icon: Icon, trend, delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ duration: 0.3, delay }}
  >
    <Card className="bg-card border-border hover:border-primary/30 transition-colors card-hover">
      <CardContent className="p-6">
        <div className="flex items-start justify-between">
          <div>
            <p className="overline mb-2">{title}</p>
            <p className="metric-number text-foreground">{value}</p>
            {subtitle && (
              <p className="text-sm text-muted-foreground mt-1">{subtitle}</p>
            )}
          </div>
          <div className="w-12 h-12 rounded-md bg-primary/10 flex items-center justify-center">
            <Icon className="w-6 h-6 text-primary" strokeWidth={1.5} />
          </div>
        </div>
        {trend !== undefined && (
          <div className="mt-4 flex items-center gap-1 text-sm">
            <TrendingUp className="w-4 h-4 text-green-500" />
            <span className="text-green-500 font-medium">{trend}%</span>
            <span className="text-muted-foreground">vs last month</span>
          </div>
        )}
      </CardContent>
    </Card>
  </motion.div>
);

const QuickAction = ({ title, description, icon: Icon, onClick, testId }) => (
  <button
    onClick={onClick}
    className="flex items-center gap-4 p-4 bg-accent/50 hover:bg-accent rounded-md transition-colors text-left w-full group"
    data-testid={testId}
  >
    <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center flex-shrink-0">
      <Icon className="w-5 h-5 text-primary" strokeWidth={1.5} />
    </div>
    <div className="flex-1 min-w-0">
      <p className="font-medium text-foreground">{title}</p>
      <p className="text-sm text-muted-foreground truncate">{description}</p>
    </div>
    <ArrowRight className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors" />
  </button>
);

export default function Dashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchStats();
  }, []);

  const fetchStats = async () => {
    try {
      const response = await getDashboardStats();
      setStats(response.data);
    } catch (error) {
      console.error("Failed to fetch stats:", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="dashboard-loading">
        <div className="skeleton h-8 w-48 rounded" />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="skeleton h-32 rounded-md" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="dashboard">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Dashboard</h1>
        <p className="text-muted-foreground mt-1">
          Overview of your guest blogging outreach performance
        </p>
      </div>

      {/* Metrics Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <MetricCard
          title="Total Websites"
          value={stats?.websites?.total || 0}
          subtitle={`${stats?.websites?.with_email || 0} with emails found`}
          icon={Globe}
          delay={0}
        />
        <MetricCard
          title="Guest Post Sites"
          value={stats?.websites?.guest_post_sites || 0}
          subtitle="Accept guest posts"
          icon={Target}
          delay={0.1}
        />
        <MetricCard
          title="Emails Sent"
          value={stats?.emails?.sent || 0}
          subtitle={`${stats?.emails?.replied || 0} replies received`}
          icon={Send}
          delay={0.2}
        />
        <MetricCard
          title="Response Rate"
          value={`${stats?.emails?.response_rate || 0}%`}
          subtitle="From sent emails"
          icon={BarChart3}
          delay={0.3}
        />
      </div>

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Quick Actions */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.4 }}
          className="lg:col-span-2"
        >
          <Card className="bg-card border-border">
            <CardHeader>
              <CardTitle className="text-lg font-semibold flex items-center gap-2">
                <Zap className="w-5 h-5 text-primary" strokeWidth={1.5} />
                Quick Actions
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <QuickAction
                title="Analyze New Website"
                description="Add a domain to get traffic metrics and find contact emails"
                icon={Globe}
                onClick={() => navigate("/websites")}
                testId="quick-action-analyze"
              />
              <QuickAction
                title="Create Campaign"
                description="Set up an outreach campaign with automated follow-ups"
                icon={Send}
                onClick={() => navigate("/campaigns")}
                testId="quick-action-campaign"
              />
              <QuickAction
                title="Generate AI Template"
                description="Create personalized email templates with AI"
                icon={Mail}
                onClick={() => navigate("/templates")}
                testId="quick-action-template"
              />
            </CardContent>
          </Card>
        </motion.div>

        {/* Average Metrics */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3, delay: 0.5 }}
        >
          <Card className="bg-card border-border h-full">
            <CardHeader>
              <CardTitle className="text-lg font-semibold">Average Metrics</CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <div>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Quality Score</span>
                  <span className="font-mono font-semibold text-primary">
                    {stats?.websites?.avg_quality || 0}
                  </span>
                </div>
                <div className="h-2 bg-accent rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-primary rounded-full transition-all duration-500"
                    style={{ width: `${stats?.websites?.avg_quality || 0}%` }}
                  />
                </div>
              </div>
              <div className="pt-4 border-t border-border">
                <p className="text-sm text-muted-foreground mb-2">Active Campaigns</p>
                <p className="text-2xl font-bold">{stats?.campaigns?.active || 0}</p>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      </div>

      {/* Call to Action */}
      {stats?.websites?.total === 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.3, delay: 0.6 }}
        >
          <Card className="bg-primary/5 border-primary/20">
            <CardContent className="p-8 text-center">
              <Globe className="w-12 h-12 text-primary mx-auto mb-4" strokeWidth={1.5} />
              <h3 className="text-xl font-semibold mb-2">Get Started</h3>
              <p className="text-muted-foreground mb-6 max-w-md mx-auto">
                Add your first website to analyze its domain metrics and find contact emails for outreach.
              </p>
              <Button 
                onClick={() => navigate("/websites")}
                data-testid="get-started-btn"
              >
                Analyze Your First Website
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>
        </motion.div>
      )}
    </div>
  );
}
